import { useQuery } from "@tanstack/react-query";
import api from "../../api/apiClient";
import { Order } from "../../types/modules/order.type";
import { OrderDetails } from "../../types/modules/orderDetail.type";
import { useAuthRole } from "../useAuth";
import { getStoreId } from "../../utils/auth";
import { OrdersMapResponse } from "../../types/modules/OrdersMap.type";

const INCOMING_ORDERS_COUNT_QUERY_KEY = (storeId?: number) => ["incoming-orders-count", storeId ?? "all"] as const;

interface UseOrdersParams {
  page?: number;
  limit?: number;
  name?: string;
  status?: string;
  storeId?: number;
  paymentMethod?: string;
  cardBrand?: string;
  orderNumber?: string;
  storeName?: string;
  customerName?: string;
  customerPhone?: string;
  pickupType?: string[];
}

export const useIncomingOrdersCount = (storeId?: number, enabled: boolean = true) => {
  const authRole = useAuthRole();
  const isStoreRole = authRole === "store";

  return useQuery({
    queryKey: INCOMING_ORDERS_COUNT_QUERY_KEY(storeId),
    queryFn: async () => {
      const params = storeId ? new URLSearchParams({ storeId: storeId.toString() }) : new URLSearchParams();
      const response = await api.get<{ count: number }>(`order/byStore/incoming/count${params.toString() ? `?${params.toString()}` : ''}`);
      return response.data.count ?? 0;
    },
    enabled: enabled && !!storeId && isStoreRole,
    staleTime: 10000,
    refetchInterval: 30000,
  });
};

export const useStoreOrders = (
  {
    page = 1,
    limit = 10,
    name = "",
    status = "",
    storeId,
    paymentMethod = "",
    cardBrand = "",
    orderNumber = "",
    storeName = "",
    customerName = "",
    customerPhone = "",
    pickupType = [],
  }: UseOrdersParams
) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  if (name) {
    queryParams.append("name", name.toString());
  }
  if (status) {
    queryParams.append("status", status.toString());
  }
  if (paymentMethod) {
    queryParams.append("paymentMethod", paymentMethod);
  }
  if (cardBrand) {
    queryParams.append("cardBrand", cardBrand);
  }
  if (orderNumber) {
    queryParams.append("orderNumber", orderNumber);
  }
  if (storeName) {
    queryParams.append("storeName", storeName);
  }
  if (customerName) {
    queryParams.append("customerName", customerName);
  }
  if (customerPhone) {
    queryParams.append("customerPhone", customerPhone);
  }
  pickupType.forEach((type) => queryParams.append("pickupType", type));
  if (storeId) {
    queryParams.append("storeId", storeId.toString());
  }

  return useQuery({
    queryKey: [
      "store-orders",
      page,
      limit,
      name,
      storeId,
      status,
      paymentMethod,
      cardBrand,
      orderNumber,
      storeName,
      customerName,
      customerPhone,
      pickupType,
    ],
    queryFn: () =>
      api.get<{ orders: Order[]; totalItems: number; totalPages: number }>(
        `order/byStore?${queryParams.toString()}`
      ).then(res => res.data),
  });
};

export const useSingleOrderDetail = (
  orderId: number | null,
  storeId: string | undefined,
  customerId: string | undefined
) => {
  let endPoint: string;
  if (storeId) {
    endPoint = `order/${orderId}/byStore?storeId=${storeId}`;
  } else if (customerId) {
    endPoint = `order/${orderId}/byCustomer?customerId=${customerId}`;
  } else {
    endPoint = `order/${orderId}/byStore`;
  }

  return useQuery({
    queryKey: ["order-detail", orderId, storeId, customerId],
    queryFn: () => api.get<OrderDetails>(endPoint).then(res => res.data),
    enabled: !!orderId,
  });
};

export const useSingleOrderCancelReasonDetails = (orderId: number, isAdmin: boolean = false) => {
  return useQuery({
    queryKey: ["order-cancel-reason-detail", orderId],
    queryFn: () =>
      api.get<{
        note: string;
        actor: { name: string; id: number };
        createdAt: string;
      }>(`audit-log/order/${orderId}`).then(res => res.data),
    enabled: !!orderId && isAdmin,
  });
};

export const useOrdersMap = (
  storeId?: number,
  enabled: boolean = true,
) => {
  const isSpecificStore = storeId !== undefined;
  const storeIdToSend = storeId ?? getStoreId();

  const endpoint = isSpecificStore
    ? `order-customer-location/store/orders-map?storeId=${storeId}`
    : "order-customer-location/store/orders-map";

  return useQuery({
    queryKey: ["orders-map", storeIdToSend],
    queryFn: () =>
      api
        .get<OrdersMapResponse>(endpoint)
        .then((res) => res.data),
    enabled: enabled && !!storeIdToSend,
    refetchInterval: 30000,
  });
};